import { ChevronRight, MapPinned, MapPinOff } from "lucide-react";

import { CLAIM_TYPES, CLAIMS_STATUS } from "@/constants";
import { formatToCurrency } from "@/lib/utils";
import type {
  CareHistoryClaimsResponseType,
  GroupedClaim,
} from "@/model/care-history";
import type { Relation } from "@/model/demographics";
import { formatDateToDayMonth, formatToMonthYr } from "@/lib/date-utils";
import { useActionLogger } from "@/services/logger/logger-hooks";
import IDENTIFIERS from "@/constants/test_id";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import ClaimStatusBadge from "./claim-status-badge";

type VisitSummaryProps = {
  groupedClaims: GroupedClaim[];
  onVisitSummaryClick: (claim: CareHistoryClaimsResponseType) => void;
  members: Record<string, Relation>;
};

const VisitSummary: React.FunctionComponent<VisitSummaryProps> = ({
  groupedClaims,
  members,
  onVisitSummaryClick,
}) => {
  const isMapviewAvailable = (item: CareHistoryClaimsResponseType) => {
    return (
      typeof item?.prvLatitude === "number" &&
      item.prvLatitude !== 0 &&
      typeof item?.prvLongitude === "number" &&
      item.prvLongitude !== 0
    );
  };

  const { activeCoverage } = useSelector(
    (state: RootState) => state?.planCoverageInfo,
  );
  const { logAction } = useActionLogger();

  return (
    <div className="scrollbar-none scrollbar-hide overflow-auto overflow-y-scroll">
      {groupedClaims?.map((groupedClaim, idx) => (
        <div key={idx} className="overflow-x-auto">
          <table className="min-w-full table-fixed border-collapse">
            <thead>
              <tr>
                {/* 1.3.1 Info and Relationships — A: a <th> with no scope leaves screen
                    readers guessing which cells it heads. This one spans the full width,
                    so it is a caption-style heading for the whole column group. */}
                <th
                  scope="colgroup"
                  colSpan={8}
                  className="text-md w-full bg-gray-100 py-2 text-center font-semibold"
                >
                  {formatToMonthYr(groupedClaim?.srvYrMonth)}
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedClaim.historyList?.map((claim, idx) => (
                <tr
                  key={idx}
                  role="row"
                  tabIndex={0}
                  aria-label={`View claim ${claim?.revClaimId ?? ""}`}
                  className="cursor-pointer border-b-2 align-top hover:bg-primary-extraLight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  onKeyDown={(event) => {
                    // 2.1.1 Keyboard — A: mirror the click behaviour. Space is prevented
                    // first so activating the row does not also scroll the page.
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.currentTarget.click();
                  }}
                  onClick={() => {
                    logAction(IDENTIFIERS.CARE_AND_CLAIMS_HISTORY.CLAIM_ITEM, {
                      claimNumber: claim?.revClaimId,
                      memberId: claim?.memberId,
                      claimCoverageCode: activeCoverage?.claimCoverageCode,
                      planId: activeCoverage?.planId,
                    });
                    return onVisitSummaryClick(claim);
                  }}
                >
                  {/* Date */}
                  <td className="w-[7%] whitespace-nowrap px-2 py-4 align-middle text-sm font-medium text-gray-900">
                    {formatDateToDayMonth(claim?.recentSvcDate)}
                  </td>

                  {/* Claim Number */}
                  {/* Added condition to render claim number
                  refer to: https://pm.zakipointhealth.com/projects/zconnect-app-development/work_packages/23207/activity for further details */}
                  {claim?.revClaimId ? (
                    <td className="w-[13%] px-2 py-4 text-left align-middle text-sm text-gray-900">
                      <p className="font-semibold">Claim Number</p>
                      <p className="text-sm font-bold">{claim?.revClaimId}</p>
                    </td>
                  ) : null}

                  {/* Name & Details */}
                  <td className="w-[22%] px-2 py-4 text-left text-sm text-gray-900">
                    <div className="font-semibold text-primary">
                      For {members?.[claim?.memberId]?.firstName}
                    </div>
                    <div className="font-bold">
                      {claim?.claimType === CLAIM_TYPES.PHARMACY.VALUE
                        ? claim?.drugName
                        : claim?.pointOfService}
                    </div>
                    <div className="font-bold">
                      {claim.prvFullName?.trim()
                        ? claim?.prvFullName
                        : "Masked Provider"}
                    </div>
                    <div> {claim?.prvAddress1}</div>
                  </td>

                  {/* Mapview Availability */}
                  <td className="w-[15%] content-center px-2 py-4 text-sm text-gray-900">
                    <div className="flex h-full flex-row">
                      <div className="mr-2 mt-auto font-medium text-gray-500">
                        Mapview{" "}
                        {isMapviewAvailable(claim)
                          ? "Available"
                          : "Unavailable"}
                      </div>
                      {isMapviewAvailable(claim) ? (
                        <MapPinned size={16} />
                      ) : (
                        <MapPinOff size={16} />
                      )}
                    </div>
                  </td>

                  {/* Member Responsibility */}
                  <td className="w-[15%] px-2 py-4 text-left align-middle">
                    <div className="text-sm font-semibold text-black">
                      Member Responsibility:
                    </div>
                    <div className="text-base font-bold text-black">
                      {claim?.totalOopPaid === CLAIMS_STATUS.PENDING
                        ? claim?.totalOopPaid
                        : formatToCurrency(parseFloat(claim?.totalOopPaid))}
                    </div>
                  </td>

                  {/* Total Charge */}
                  <td className="w-[10%] px-2 py-4 text-left align-middle text-sm text-gray-900">
                    <p className="font-semibold">Total Charge:</p>
                    <p className="text-base font-bold text-black">
                      {claim?.totalOopPaid === CLAIMS_STATUS.PENDING
                        ? claim?.totalOopPaid
                        : formatToCurrency(parseFloat(claim?.billedAmt))}
                    </p>
                  </td>

                  {/* Status */}
                  <td className="w-[13%] px-2 py-4 text-center align-middle">
                    <ClaimStatusBadge
                      revAdjudicationDesc={claim?.revAdjudicationDesc}
                      statusDefinition={claim?.statusDefinition}
                    />
                  </td>

                  {/* Arrow */}
                  <td className="w-[5%] text-left align-middle">
                    <ChevronRight className="ml-2" size={16} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default VisitSummary;
